import {
  getDayOfWeek,
  iife,
  isSome,
  Maybe,
  v2_Branch,
} from '@crocodille/common';
import cn from 'classnames';
import { addDays, startOfWeek } from 'date-fns';
import Link from 'next/link';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsomorphicLayoutEffect as useLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';
import Button from '../../elements/button/button';
import MailIcon from '../../icons/mail-icon';
import PhoneIcon from '../../icons/phone-icon';
import PinIcon from '../../icons/pin-icon';
import Image from '@components/elements/image/image';
import styles from './branch-carousel.module.scss';
import { StaticImageData } from 'next/image';
import creperieImage from './creperie.svg';
import driveThruImage from './drive-thru.svg';
import easyPickupImage from './easy-pickup.svg';
import electricChargerImage from './electric-charger.svg';
import iceCreamImage from './ice-cream.svg';
import parkingPickupImage from './parking-pickup.svg';
import playgroundImage from './playground.svg';

const TAG_IMAGE_MAP: Record<string, StaticImageData> = {
  ['EASY-PICKUP']: easyPickupImage,
  ['CRÊPERIE']: creperieImage,
  ['ZMRZLINA']: iceCreamImage,
  ['DRIVE-THRU']: driveThruImage,
  ['PARKING-PICKUP']: parkingPickupImage,
  ['DĚTSKÉ HŘIŠTĚ']: playgroundImage,
  ['ELEKTRO NABÍJEČKA']: electricChargerImage,
};
import { formatDistance } from '@crocodille/bbdomu-i18n';

const BranchStatus: React.FC<{
  readonly isOpen: boolean;
  readonly isAvailable: boolean;
  readonly isNoCapacity: boolean;
}> = ({ isOpen, isAvailable, isNoCapacity }) => {
  const { t } = useTranslation();

  const status = iife(() => {
    if (isNoCapacity) {
      return 'no_capacity';
    } else if (isOpen) {
      if (isAvailable) {
        return 'open';
      } else {
        return 'busy';
      }
    } else {
      return 'closed';
    }
  });

  return (
    <span className={cn(styles.branchStatus, styles[status])}>
      {t(`branch_status.${status}`)}
    </span>
  );
};

export interface BranchCarouselProps {
  readonly children: readonly React.ReactElement<
    BranchCarouselItemProps,
    typeof Item
  >[];
}

const BranchCarouselElement: React.FC<
  React.PropsWithChildren<BranchCarouselProps>
> = ({ children }) => {
  const listRef = useRef<HTMLUListElement>(null);

  const childOrder = children.map((child) => child.key).join();

  useLayoutEffect(() => {
    listRef.current?.scroll?.({
      left: 0,
    });
  }, [childOrder]);

  return (
    <ul ref={listRef} className={cn(styles.container)}>
      {children}
    </ul>
  );
};

export interface BranchCarouselItemProps {
  readonly as?: 'li' | 'div';
  readonly code: string;
  readonly label: string;
  readonly address: string;
  readonly availability: 'open' | 'closed' | 'busy' | 'noCapacity';
  readonly openFrom?: Maybe<string>;
  readonly openTo?: Maybe<string>;
  readonly deliveryFrom?: Maybe<string>;
  readonly deliveryTo?: Maybe<string>;
  readonly distance?: Maybe<number>;
  readonly detailOpen?: boolean;
  readonly openingHours: v2_Branch['openingHours'];
  readonly email?: Maybe<string>;
  readonly phoneNumber?: Maybe<string>;
  readonly onDetailOpen: () => void;
  readonly onShippingMethodPicked: (
    branchCode: string,
    shippingMethodId: number,
  ) => void;
  readonly detailHref: string;
  readonly shippingMethods: readonly {
    readonly id: number;
    readonly label: string;
    readonly disabled: boolean;
  }[];
  readonly properties: readonly string[];
  readonly showDetailButton?: boolean;
}

const Item: React.FC<React.PropsWithChildren<BranchCarouselItemProps>> = ({
  showDetailButton = true,
  ...props
}) => {
  const { t } = useTranslation();

  const weekdays = useMemo(() => {
    const days = [];
    const start = startOfWeek(Date.now(), { weekStartsOn: 1 });

    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }

    return days;
  }, []);

  const isOpenToday = isSome(props.openFrom) && isSome(props.openTo);

  const isDeliveringToday =
    isSome(props.deliveryFrom) && isSome(props.deliveryTo);

  const itemRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (props.detailOpen && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [props.detailOpen]);

  const Tag = props.as ?? 'li';

  const addressWithoutPostcode = props.address.replace(/,\s*\d{5}$/, '').trim();

  return (
    <Tag
      ref={itemRef as never}
      className={cn(styles.item, {
        [styles.isClosed]: props.availability !== 'closed',
        [styles.isDetailOpen]: props.detailOpen,
      })}
    >
      <div className={styles.itemInner}>
        <div className={styles.label}>
          {props.label}
          <p className={styles.address}>{addressWithoutPostcode}</p>
        </div>
        <span className={cn(styles.status)}>
          <BranchStatus
            isAvailable={
              props.availability === 'open' || props.availability === 'busy'
            }
            isOpen={props.availability === 'open'}
            isNoCapacity={props.availability === 'noCapacity'}
          />
        </span>
        <p className={cn(styles.shortDescription, styles.isMobile)}>
          <br />
          {isOpenToday
            ? t('branch.opening_hours', {
                from: props.openFrom,
                to: props.openTo,
              })
            : t('branch.opening_hours_closed')}
          <br />
          {isDeliveringToday
            ? t('branch.delivery_hours', {
                from: props.deliveryFrom,
                to: props.deliveryTo,
              })
            : t('branch.delivery_hours_closed')}
        </p>
        <div className={styles.tags}>
          {props.properties.map((property) => {
            const src = TAG_IMAGE_MAP[property];

            if (src) {
              return (
                <div
                  key={property}
                  className={styles.tag}
                  style={{
                    ['--aspect-ratio']: (src.width / src.height).toPrecision(
                      10,
                    ),
                  }}
                >
                  <Image
                    src={src}
                    alt={property}
                    className={styles.tagImage}
                    layout="fill"
                  />
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
        <div className={styles.distance}>
          <PinIcon className={styles.icon} />
          <span className={styles.inner}>
            {isSome(props.distance)
              ? formatDistance(props.distance)
              : t('branch.unknown_distance')}
          </span>
          <p className={cn(styles.shortDescription, styles.isDesktop)}>
            {isOpenToday
              ? t('branch.opening_hours', {
                  from: props.openFrom,
                  to: props.openTo,
                })
              : t('branch.opening_hours_closed')}
            <br />
            {isDeliveringToday
              ? t('branch.delivery_hours', {
                  from: props.deliveryFrom,
                  to: props.deliveryTo,
                })
              : t('branch.delivery_hours_closed')}
          </p>
        </div>

        {showDetailButton ? (
          <Link
            href={props.detailHref}
            prefetch={false}
            className={styles.details}
            onClick={(e) => {
              props.onDetailOpen();
              e.preventDefault();
            }}
          >
            <span
              className={cn(styles.detailButtonIcon, {
                [styles.plus]: !props.detailOpen,
                [styles.minus]: props.detailOpen,
              })}
            >
              {props.detailOpen ? '-' : '+'}
            </span>
          </Link>
        ) : null}
        <div className={styles.contact}>
          {props.detailOpen ? (
            <>
              <h4 className={styles.contactTitle}>{t('branch.contact')}</h4>
              <p>
                {props.phoneNumber ? (
                  <>
                    <PhoneIcon className={styles.iconPhone} />
                    <a href={`tel:${props.phoneNumber}`}>{props.phoneNumber}</a>
                    <br />
                  </>
                ) : null}
                {props.email ? (
                  <>
                    <MailIcon className={styles.iconMail} />
                    <a href={`mailto:${props.email}`}>{props.email}</a>
                    <br />
                  </>
                ) : null}
              </p>
            </>
          ) : null}
          {props.detailOpen ? (
            <>
              <h4 className={styles.openingHoursTitle}>
                {t('branch.opening_hours_title')}
              </h4>
              <table>
                <tbody>
                  {weekdays.map((date) => {
                    const dayOfWeek = getDayOfWeek(date);
                    const open = props.openingHours[dayOfWeek]?.openFrom;
                    const close = props.openingHours[dayOfWeek]?.openTo;
                    return (
                      <tr key={dayOfWeek}>
                        <th>{t(`day_of_week.${dayOfWeek}`)}</th>
                        <td>
                          {open && close
                            ? `${open} - ${close}`
                            : t('branch.closed')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : null}
        </div>

        <div className={styles.shipping}>
          {props.shippingMethods.map(({ label, id, disabled }) => (
            <Button
              round
              key={id}
              disabled={disabled}
              variant={props.detailOpen ? 'basic' : 'primary'}
              symmetrical={false}
              onClick={() => props.onShippingMethodPicked(props.code, id)}
              className={styles.shippingButton}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </Tag>
  );
};

const BranchCarousel = Object.assign(BranchCarouselElement, { Item });

export default BranchCarousel;
